"use client";

import { useState } from "react";
import { Play, CheckCircle, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";

export function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [messageTone, setMessageTone] = useState<"success" | "info" | "error">("success");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim() || loading) {
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const response = await api.post<{
        id: string;
        alreadySubscribed: boolean;
        message: string;
      }>("/api/newsletter/subscribe", {
        email,
        source: "storefront_footer",
      });

      setMessageTone(response.data.alreadySubscribed ? "info" : "success");
      setMessage(response.data.message);

      if (!response.data.alreadySubscribed) {
        setEmail("");
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } };
      const validationMessage = axiosError.response?.data?.errors
        ? Object.values(axiosError.response.data.errors)[0]?.[0]
        : null;

      setMessageTone("error");
      setMessage(validationMessage ?? axiosError.response?.data?.message ?? "Abonelik sirasinda bir hata olustu.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full md:w-auto space-y-3">
      <style>{`
        .nl-input::placeholder { color: var(--sf-footer-banner-desc-color, #6b7280); }
      `}</style>
      <form onSubmit={(e) => void handleSubmit(e)} className="flex items-center gap-2 w-full md:w-auto">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="E-posta adresiniz"
          required
          disabled={loading}
          className="nl-input h-10 px-4 text-base md:text-sm bg-white/10 border border-white/20 text-white rounded-[var(--sf-radius)] focus:outline-none focus:border-white/50 min-w-0 w-full md:w-64 disabled:opacity-70"
        />
        <button
          type="submit"
          disabled={loading}
          className="group h-10 px-5 bg-white text-gray-950 text-sm font-semibold rounded-[var(--sf-radius)] hover:bg-gray-100 transition-colors shrink-0 flex items-center gap-2 disabled:opacity-70"
        >
          {loading ? "Gönderiliyor" : "Abone Ol"}
          <Play className="h-3 w-3 fill-current stroke-0 transition-transform group-hover:translate-x-0.5" />
        </button>
      </form>

      {message && (
        <div
          className={
            messageTone === "error"
              ? "flex items-center gap-2 text-red-300"
              : messageTone === "info"
                ? "flex items-center gap-2 text-amber-300"
                : "flex items-center gap-2 text-emerald-400"
          }
        >
          {messageTone === "error" ? <AlertCircle className="h-5 w-5" /> : <CheckCircle className="h-5 w-5" />}
          <span className="text-sm font-medium">{message}</span>
        </div>
      )}
    </div>
  );
}
